import { IdsFilter, allRows } from '@components/content-table/content-table';

import { downloadBlob } from './download-file-by-url';

export interface ExportStatus {
	progress: number;
	completed: boolean;
}

// The file itself is built in a couple of seconds; the delay a user sees is
// almost entirely how long it takes this poll to notice.
const pollIntervalMs = 5000;

// A transient failure should not end the export, but a permanent one has to
// surface instead of leaving the toaster spinning.
const maxFailedPolls = 3;

export class ExportController<T extends { ids?: null | number[] }> {
	constructor(
		private init: (filter: T) => Promise<string>,
		private cancel: (id: string) => Promise<void>,
		private checkStatus: (id: string) => Promise<ExportStatus>,
		/**
		 * Returns the finished file itself. It is served by the API rather than by
		 * a presigned storage link, which a browser cannot read with fetch.
		 */
		private getFile: (id: string) => Promise<Blob>
	) {}

	async export(
		ids: IdsFilter,
		filter: T,
		fileName: string,
		handleProgressUpdate: (nextProgress: number) => void,
		handleDownloaded: () => void,
		signal: AbortSignal,
		handleFailed?: () => void
	) {
		let timeout: null | NodeJS.Timeout = null;
		let id: null | string = null;
		let created = false;
		let failedPolls = 0;

		signal.onabort = () => {
			if (timeout) {
				clearTimeout(timeout);
			}
			if (id && !created) {
				this.cancel(id);
			}
		};

		const mergedFilter = { ...filter, ids: ids === allRows ? undefined : ids };
		if (signal.aborted) {
			return;
		}
		id = await this.init(mergedFilter);
		if (signal.aborted) {
			this.cancel(id);
			return;
		}
		let progress = 0;
		const retry = () => {
			timeout = setTimeout(async () => {
				timeout = null;
				if (!id) {
					return;
				}
				if (signal.aborted) {
					this.cancel(id);
					return;
				}

				// Everything below used to run unguarded. A rejected poll became an
				// unhandled rejection, the loop simply stopped and the progress
				// toaster hung forever with no hint of what went wrong.
				try {
					const { completed, progress: nextProgress } = await this.checkStatus(id);
					if (signal.aborted) {
						if (completed) {
							return;
						}
						this.cancel(id);
						return;
					}
					if (nextProgress > progress) {
						progress = nextProgress;
						handleProgressUpdate(nextProgress);
					}
					if (completed) {
						created = true;
						const file = await this.getFile(id);
						if (signal.aborted) {
							return;
						}
						downloadBlob(file, fileName);
						handleDownloaded();

						return;
					}

					failedPolls = 0;
					retry();
				} catch (error) {
					if (signal.aborted) {
						return;
					}

					failedPolls += 1;
					console.error(`Export poll failed (${failedPolls}/${maxFailedPolls})`, error);

					if (failedPolls >= maxFailedPolls) {
						handleFailed?.();

						return;
					}

					retry();
				}
			}, pollIntervalMs);
		};

		retry();
	}
}
